import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Database, Brain, Shield, Globe, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Loader2, HardDrive } from 'lucide-react';
import { getAuthHeaders } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';

import { BACKEND_URL } from '../config/api';

const STATUS_CONFIG = {
  healthy: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Healthy' },
  degraded: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Degraded' },
  error: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Error' },
  missing: { icon: XCircle, color: 'text-gray-400', bg: 'bg-gray-50', label: 'Not Configured' },
};

const SERVICE_META = {
  mongodb: { label: 'MongoDB', icon: Database, description: 'Primary database for all app data' },
  chromadb: { label: 'ChromaDB', icon: Brain, description: 'Vector store for YouTube transcript search' },
  gemini_llm: { label: 'Gemini LLM', icon: Brain, description: 'AI text generation and image analysis' },
  tor_proxy: { label: 'Tor Proxy', icon: Shield, description: 'YouTube transcript proxy (bypasses IP blocks)' },
  image_search: { label: 'Image Search', icon: Globe, description: 'Google/Bing product image search' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.error;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <Icon size={13} />
      {cfg.label}
    </span>
  );
}

function TorDetail({ detail }) {
  if (!detail || typeof detail === 'string') return <p className="text-sm text-gray-soft">{detail}</p>;
  const checks = [
    { key: 'binary_installed', label: 'Tor binary installed' },
    { key: 'process_running', label: 'Tor process running', extra: detail.pid ? `(PID ${detail.pid})` : '' },
    { key: 'socks_proxy_reachable', label: 'SOCKS proxy on port 9050' },
    { key: 'control_port_reachable', label: 'Control port on 9051 (circuit rotation)' },
  ];
  return (
    <div className="space-y-2 mt-2">
      {checks.map(c => (
        <div key={c.key} className="flex items-center gap-2 text-sm" data-testid={`tor-check-${c.key}`}>
          {detail[c.key]
            ? <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
            : <XCircle size={14} className="text-red-500 flex-shrink-0" />}
          <span className={detail[c.key] ? 'text-charcoal' : 'text-red-600'}>{c.label} {c.extra}</span>
        </div>
      ))}
      <div className="flex items-center gap-2 text-sm" data-testid="tor-check-youtube_fetch">
        {detail.youtube_fetch_test === 'pass'
          ? <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
          : detail.youtube_fetch_test === 'fail'
            ? <XCircle size={14} className="text-red-500 flex-shrink-0" />
            : <AlertTriangle size={14} className="text-gray-400 flex-shrink-0" />}
        <span className={detail.youtube_fetch_test === 'pass' ? 'text-charcoal' : detail.youtube_fetch_test === 'fail' ? 'text-red-600' : 'text-gray-400'}>
          YouTube transcript fetch test: {detail.youtube_fetch_test}
          {detail.test_transcript_segments && ` (${detail.test_transcript_segments} segments)`}
        </span>
      </div>
      {detail.youtube_fetch_error && (
        <div className="mt-1 p-2 bg-red-50 rounded text-xs text-red-700 font-mono break-all" data-testid="tor-youtube-error">
          {detail.youtube_fetch_error}
        </div>
      )}
    </div>
  );
}

function ServiceCard({ serviceKey, data }) {
  const meta = SERVICE_META[serviceKey] || { label: serviceKey, icon: Activity, description: '' };
  const Icon = meta.icon;
  const isTor = serviceKey === 'tor_proxy';

  return (
    <div className="bg-white rounded-xl border border-beige p-5" data-testid={`service-card-${serviceKey}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center">
            <Icon size={20} className="text-charcoal" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-charcoal">{meta.label}</h3>
            <p className="text-xs text-gray-soft">{meta.description}</p>
          </div>
        </div>
        <StatusBadge status={data.status} />
      </div>
      {isTor ? (
        <TorDetail detail={data.detail} />
      ) : (
        <p className="text-sm text-gray-soft mt-1">{data.detail}</p>
      )}
    </div>
  );
}

export default function AdminServices() {
  const [health, setHealth] = useState(null);
  const [r2Status, setR2Status] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [error, setError] = useState(null);
  const [checkedAt, setCheckedAt] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = getAuthHeaders();
      const [healthRes, r2Res] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/services/health`, { credentials: 'include', headers }),
        fetch(`${BACKEND_URL}/api/admin/r2-status`, { credentials: 'include', headers }),
      ]);
      if (healthRes.ok) {
        const data = await healthRes.clone().json();
        setHealth(data.services);
        setCheckedAt(data.checked_at);
      }
      if (r2Res.ok) {
        setR2Status(await r2Res.clone().json());
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const runMigration = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/migrate-images-to-r2`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.clone().json();
      setMigrationResult(data);
      fetchHealth();
    } catch (err) {
      setMigrationResult({ status: 'error', message: err.message });
    } finally {
      setMigrating(false);
    }
  };

  const overallStatus = health
    ? Object.values(health).every(s => s.status === 'healthy')
      ? 'All systems operational'
      : Object.values(health).some(s => s.status === 'error')
        ? 'Some services have issues'
        : 'Some services degraded'
    : null;

  const overallColor = health
    ? Object.values(health).every(s => s.status === 'healthy')
      ? 'text-emerald-600'
      : 'text-amber-600'
    : '';

  return (
    <AdminLayout>
      <div data-testid="admin-services">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-medium text-charcoal">Service Health</h1>
            {overallStatus && (
              <p className={`text-sm mt-1 ${overallColor}`}>{overallStatus}</p>
            )}
          </div>
          <button
            onClick={fetchHealth}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-charcoal text-white text-sm rounded-lg hover:bg-charcoal/90 transition-colors disabled:opacity-50"
            data-testid="refresh-health-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Checking...' : 'Refresh'}
          </button>
        </div>

        {checkedAt && (
          <p className="text-xs text-gray-soft mb-4" data-testid="health-checked-at">
            Last checked: {new Date(checkedAt).toLocaleString()}
          </p>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 rounded-lg p-4 mb-4 text-sm" data-testid="health-error">
            Failed to fetch service health: {error}
          </div>
        )}

        {loading && !health ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-gray-soft" size={32} />
          </div>
        ) : health ? (
          <div className="grid gap-4">
            {Object.entries(health).map(([key, data]) => (
              <ServiceCard key={key} serviceKey={key} data={data} />
            ))}

            {/* R2 Storage Card */}
            {r2Status && (
              <div className="bg-white rounded-xl border border-beige p-5" data-testid="service-card-r2">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center">
                      <HardDrive size={20} className="text-charcoal" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-charcoal">Cloudflare R2 Storage</h3>
                      <p className="text-xs text-gray-soft">CDN-backed image storage (11-nines durability)</p>
                    </div>
                  </div>
                  <StatusBadge status={r2Status.r2_configured ? 'healthy' : 'error'} />
                </div>
                <div className="space-y-2 mt-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-soft">Environment</span>
                    <span className={`font-medium ${r2Status.detected_env === 'production' ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {r2Status.detected_env}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-soft">Bucket</span>
                    <span className="font-mono text-xs text-charcoal">{r2Status.r2_bucket}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-soft">Public URL</span>
                    <span className="font-mono text-xs text-charcoal">{r2Status.r2_public_url}</span>
                  </div>
                  <div className="border-t border-beige my-2" />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-soft">Total images</span>
                    <span className="font-medium text-charcoal">{r2Status.total_images}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-soft">Migrated to current env</span>
                    <span className={`font-medium ${r2Status.migrated_to_current_env === r2Status.total_images ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {r2Status.migrated_to_current_env} / {r2Status.total_images}
                    </span>
                  </div>
                  {r2Status.stash_items_with_old_urls > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-soft">Stash items with old URLs</span>
                      <span className="font-medium text-amber-600">{r2Status.stash_items_with_old_urls}</span>
                    </div>
                  )}
                  {r2Status.needs_migration && (
                    <div className="mt-3 space-y-2">
                      <div className="p-2 bg-amber-50 rounded-lg text-xs text-amber-700 flex items-center gap-2">
                        <AlertTriangle size={14} />
                        Migration needed — some images not yet on current R2 environment
                      </div>
                      <button
                        onClick={runMigration}
                        disabled={migrating}
                        className="w-full py-2.5 bg-charcoal text-white text-sm font-medium rounded-lg hover:bg-charcoal/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="run-migration-btn"
                      >
                        {migrating ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Migrating images...
                          </>
                        ) : (
                          <>
                            <HardDrive size={14} />
                            Run Migration to {r2Status.detected_env}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  {migrationResult && (
                    <div className={`mt-2 p-3 rounded-lg text-xs ${migrationResult.status === 'complete' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} data-testid="migration-result">
                      {migrationResult.status === 'complete' ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 font-medium"><CheckCircle2 size={14} /> Migration complete</div>
                          <div>Uploaded: {migrationResult.migrated} images</div>
                          <div>References updated: {migrationResult.refs_updated}</div>
                          {migrationResult.failed > 0 && <div className="text-amber-700">Failed: {migrationResult.failed}</div>}
                          <div className="text-gray-500">MongoDB originals: {migrationResult.mongodb_originals}</div>
                        </div>
                      ) : (
                        <div>{migrationResult.message || 'Migration failed'}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </AdminLayout>
  );
}
